import { Button } from "../components/ui/button";
import { Head } from "@inertiajs/react";
// @ts-expect-error
import { ModalLink } from "@inertiaui/modal-react";
import { TrackEventCard } from "../components/TrackEventCard";
import { RaceWeekendCard } from "../components/RaceWeekendCard";
import { Pagination } from "../components/Pagination";
import AuthLayout from "../layouts/AuthLayout";
import { InertiaPageComponent } from "@/types";
import {
  User,
  HomePage,
  RaceWeekend,
  IndividualEvent,
} from "@/types/generated";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { useActionCable } from "@/lib/useActionCable";
import { toast } from "sonner";
import { AboutCard } from "../components/AboutCard";

type TrackEventItem = RaceWeekend | IndividualEvent;

function isRaceWeekend(item: TrackEventItem): item is RaceWeekend {
  return item.type === "race_weekend";
}

function isIndividualEvent(item: TrackEventItem): item is IndividualEvent {
  return item.type === "individual";
}

export interface HomePageProps extends Omit<HomePage, "track_events"> {
  track_events: TrackEventItem[];
  auth?: {
    user: User;
  };
}

const Home: InertiaPageComponent<HomePageProps> = ({
  track_events,
  pagination,
  auth,
  ...props
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [events, setEvents] = useState<TrackEventItem[]>(track_events);

  // Only connect to ActionCable if user is authenticated
  useActionCable(
    auth?.user
      ? {
        channel: "TrackEventsChannel",
        feed_user_id: auth.user.id,
      }
      : null,
    {
      onReceived: (data) => {
        if (data.type === "new_track_event") {
          const trackEvent = data.track_event;

          // Create proper IndividualEvent type with event property
          const newEvent: IndividualEvent = {
            type: "individual",
            event: trackEvent,
            ...trackEvent, // Spread to support backwards compatibility
            comments: [], // Initialize with empty comments array
          };

          // Add new event to the top of the list
          setEvents((prev) => [newEvent, ...prev]);

          // Show toast notification
          toast.info("New Track Event", {
            description: `${trackEvent.user?.full_name} posted a new ${trackEvent.session_type} session`,
          });
        }
      },
    }
  );

  const handleRefreshiRacingData = async () => {
    if (!props.has_iracing_account) return;

    setIsRefreshing(true);
    try {
      const response = await fetch("/iracing/refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        // Show success message
        toast.success("iRacing data refresh started!", {
          description: data.message,
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Failed to refresh iRacing data:", errorData);
        toast.error("Failed to refresh iRacing data", {
          description: errorData.error || "Unknown error. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error refreshing iRacing data:", error);
      toast.error("Error", {
        description: "Error refreshing iRacing data. Please try again.",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const feedContent = (
    <>
      {events && events.length > 0 ? (
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-1">
              Recent Activity
            </h2>
            <p className="text-gray-600 font-medium mb-6">
              {auth?.user
                ? "Latest track events from racers you follow"
                : "Latest public track events"}
            </p>
          </div>

          <div className="space-y-2">
            {events.map((item) => {
              if (isRaceWeekend(item)) {
                return (
                  <RaceWeekendCard
                    key={`race_weekend_${item.iracing_session_id}`}
                    raceWeekend={item}
                  />
                );
              } else if (isIndividualEvent(item)) {
                return (
                  <TrackEventCard
                    key={`individual_${item.id}`}
                    trackEvent={item}
                  />
                );
              }
              return null;
            })}
          </div>

          {pagination && (
            <div className="mt-8">
              <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                totalCount={pagination.total_count}
                perPage={pagination.per_page}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gray-50/50 min-h-[60vh] h-[calc(100dvh-8rem)] overflow-y-hidden flex items-center justify-center -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl w-full text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Racing Activity Yet
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">
              Connect your iRacing account to automatically import your
              sessions and lap times. Start following other racers to see
              their track events!
            </p>
            <a href="/auth/iracing">
              <Button variant="default">
                Connect iRacing Account
              </Button>
            </a>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {auth?.user ? (
          <div>
            {props.has_iracing_account && (
              <Alert
                variant="default"
                className="flex w-full items-center justify-between mb-4"
              >
                <span>
                  <AlertTitle>iRacing Connected</AlertTitle>
                  <AlertDescription>
                    {props.iracing_events_count} sessions imported. Click refresh
                    to get latest data.
                  </AlertDescription>
                </span>
                <Button
                  variant="outline"
                  onClick={handleRefreshiRacingData}
                  disabled={isRefreshing}
                  className="ml-4"
                >
                  <RefreshCw
                    className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""
                      }`}
                  />
                  {isRefreshing ? "Refreshing..." : "Refresh Data"}
                </Button>
              </Alert>
            )}
            {feedContent}
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-12 items-start mt-8">
            <div className="lg:sticky lg:top-32 order-1">
              <AboutCard />
            </div>
            <div className="order-2">
              {feedContent}
            </div>
          </div>
        )}
      </main>
    </>
  );
};


Home.layout = (page) => <AuthLayout>{page}</AuthLayout>;

export default Home;
